import type { DocPage } from '../types'
import { media } from '../media'


export const mapping: DocPage = {
  id: 'mapping',
  title: 'Mapping overview',
  description:
    'Hub for mapped vs scratch transfers, the vertical Connect rail, mapping toolbar (F/V/import/export/clear/inactive), and links to fixed mapping, value mapping, Auto Map, and Connect-ID.',
  category: 'Mapping',
  sections: [
    { id: 'types', title: 'Mapped vs scratch' },
    { id: 'rail', title: 'Vertical action rail' },
    { id: 'connect', title: 'Connect workflow' },
    { id: 'reorder', title: 'Reorder mappings' },
    { id: 'toolbar', title: 'Mapping toolbar' },
    { id: 'inactive', title: 'Inactive mappings' },
    { id: 'import-export', title: 'Import / export mapping' },
    { id: 'deep-links', title: 'Deep-dive chapters' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'types',
      text: 'Mapped vs scratch',
    },
    {
      type: 'table',
      headers: ['Type', 'Meaning', 'Mapping tools'],
      rows: [
        [
          'MAPPED',
          'You explicitly map source collections/attributes to an existing target model.',
          'Connect, Auto Map, fixed mapping, value mapping, Connect-ID / Connect-Delete.',
        ],
        [
          'SCRATCH',
          'X-Translator creates/writes target schema from the source. No predefined target tree mapping.',
          'Fixed mappings on source attributes still apply (Set fixed value). Value mapping is unavailable.',
        ],
      ],
    },
    {
      type: 'figure',
      src: media.mappingExample,
      alt: 'Mapping panel overview',
      caption:
        'Figure: Mapping tree — Value Mappings folder, Collection Mappings, and attribute links such as Status → L_status.',
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Value mapping and scratch',
      text: 'Value mapping is not available for scratch transfers. Use fixed values on logical or source attributes instead when you need constants on scratch output.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'rail',
      text: 'Vertical action rail',
    },
    {
      type: 'figure',
      src: media.verticalMiddlePanel,
      alt: 'Vertical mapping action buttons',
      caption:
        'Figure: Middle rail buttons used while building mappings — Connect through Clear.',
      size: 'rail',
    },
    {
      type: 'table',
      headers: ['Button', 'Role in mapping'],
      rows: [
        ['Connect', 'Create a mapping between the selected source and target nodes.'],
        [
          'Connect-ID',
          'Mark attribute mappings as update keys (match existing target rows). See Connect keys.',
        ],
        [
          'Connect-Delete',
          'Mark attribute mappings as delete keys (remove matching target rows).',
        ],
        ['Disconnect', 'Remove the selected mapping.'],
        ['Auto Map', 'Open the Auto Map window for suggestions. See Auto Map.'],
        ['Transfer', 'Leave mapping and open Transfer Review.'],
        ['Clear', 'Clear mappings / selection context as offered by the UI.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'connect',
      text: 'Connect workflow',
    },
    {
      type: 'steps',
      items: [
        'Load Source and Target models (or configure a scratch Target destination).',
        'For mapped transfers: select a source collection and a matching target collection.',
        'Click Connect in the vertical rail.',
        'Expand the connected collections and Connect attribute pairs the same way.',
        'Optionally drag a mapping to preview a new order (see Reorder mappings), or use Move up / Move down in the context menu.',
        'Optionally run Auto Map for bulk attribute suggestions.',
        'Add fixed mappings (F) and value mappings (V) where needed.',
        'Configure Connect-ID / Connect-Delete and create-on-no-match if updating/deleting.',
        'Use Disconnect to remove incorrect links; Remove inactive mapping to clean stale ones.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'reorder',
      text: 'Reorder mappings',
    },
    {
      type: 'paragraph',
      text: 'Drag the grip at the left of a collection or attribute mapping. A dimmed copy follows the pointer while the rest of the tree slides to preview the new order. Dragging a collection mapping also moves its attribute mappings with it. Release to save the order, or press Escape to cancel. You can also right-click a mapping and choose Move up / Move down.',
    },
    {
      type: 'paragraph',
      text: 'The order is saved with the mapping. It controls output column order in newly created files and tables, and the 1-based Connect-ID / Connect-Delete key numbers shown in the tree. Reordering those key rows changes which field is 1, 2, 3 in a custom predicate unless you edit the expression afterwards.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'toolbar',
      text: 'Mapping toolbar',
    },
    {
      type: 'table',
      headers: ['Control', 'Label / tip', 'Notes'],
      rows: [
        ['F', 'Add fixed mapping / Set fixed value for logical attribute', 'Scratch uses Set fixed value.'],
        ['V', 'Add value mapping', 'Disabled on scratch transfers.'],
        ['Key icon', 'Global create-on-no-match', 'Mapped Connect-ID only; disabled until ≥1 Connect-ID mapping.'],
        ['Import', 'Import mapping', 'Load a previously exported mapping definition.'],
        ['Export', 'Export mapping', 'Save the current mapping for reuse or backup.'],
        ['Brush', 'Remove inactive mapping', 'Drop mappings that no longer match the loaded models.'],
        ['Clear', 'Clear mapping', 'Remove mappings from the panel.'],
        ['Search', 'Search Mapping', 'Find collections/attributes in the mapping tree.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'inactive',
      text: 'Inactive mappings',
    },
    {
      type: 'paragraph',
      text: 'After you reload models with different filters, renamed tables, or changed CAD modes, some mappings may become inactive. Use Remove inactive mapping to clean the panel before transfer. Re-Connect or re-run Auto Map for replacements.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'import-export',
      text: 'Import / export mapping',
    },
    {
      type: 'list',
      items: [
        'Export mapping after a carefully reviewed setup so colleagues can reuse it.',
        'Import mapping into a workspace with compatible Source/Target schemas.',
        'After import, verify Connect-ID predicates, value mapping references, and inactive items.',
        'Pair mapping files with Transfer Profiles and saved Model Configurations for full repeatability.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'deep-links',
      text: 'Deep-dive chapters',
    },
    {
      type: 'definitions',
      items: [
        {
          term: 'Fixed mapping',
          definition:
            'Constant values by type (text/char/integer/decimal/boolean/date/enum). See Fixed mapping.',
        },
        {
          term: 'Value mapping',
          definition:
            'Conditional rules with operators and Else modes. See Value mapping.',
        },
        {
          term: 'Auto Map',
          definition:
            'Strict / Balanced / Exploratory suggestions in a separate window. See Auto Map.',
        },
        {
          term: 'Connect keys',
          definition:
            'Connect-ID, Connect-Delete, predicates, create-on-no-match. See Connect keys.',
        },
        {
          term: 'Running transfers',
          definition:
            'Transfer Review, MAPPED vs SCRATCH execution rules. See Running transfers.',
        },
      ],
    },
  ],
}
